import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { Button } from "components/ui/button";

import { CAMPAIGN_CLOSED_WITHDRAWN_ROUTES } from "../constants/campaignClosed";
import { CAMPAIGN_QUIET_CONTROL_CLASS } from "../constants/campaignTheme";
import {
  CHARITY_JOIN_NAV_LABEL,
  CHARITY_REPORT_NAV_LABEL,
  CHARITY_RIDES_LABEL,
} from "../constants/charityActionLabels";
import { CHARITY_SIGN_OUT_LABEL } from "../constants/charitySession";
import { useCharity } from "../context/CharityContext";
import { useIsCampaignClosed } from "../hooks/useIsCampaignClosed";
import { CharityRoute } from "../types";

import { CharityLogo } from "./CharityLogo";

interface CharityNavLink {
  label: string;
  route: CharityRoute;
}

// How To trails the pages a reader comes back to, being the one they consult
// once and then stop needing. The way in or out of the account comes after it in
// both lists — Sign In here, the sign-out control beside the participant links —
// so the end of the bar means the same thing whoever is looking at it.
const PARTICIPANT_NAV_LINKS: CharityNavLink[] = [
  { label: "Campaign", route: CharityRoute.Campaign },
  { label: "Leaderboard", route: CharityRoute.Leaderboard },
  { label: "How To", route: CharityRoute.HowItWorks },
  { label: CHARITY_REPORT_NAV_LABEL, route: CharityRoute.Report },
  { label: CHARITY_RIDES_LABEL, route: CharityRoute.Reports },
];

// Join sits beside Sign In because a visitor arrives as one of two people: one
// who already has a household and one who does not. Without it the second had
// no way in from the bar at all, and had to find the join page through a line
// of small print under the sign-in form.
const VISITOR_NAV_LINKS: CharityNavLink[] = [
  { label: "Campaign", route: CharityRoute.Campaign },
  { label: "Leaderboard", route: CharityRoute.Leaderboard },
  { label: "How To", route: CharityRoute.HowItWorks },
  { label: CHARITY_JOIN_NAV_LABEL, route: CharityRoute.Join },
  { label: "Sign In", route: CharityRoute.SignIn },
];

const linkClassName = (isActive: boolean): string =>
  `whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition ${
    isActive ? "bg-[#E66E1A] text-[#131315]" : "text-[#A2A09C] hover:bg-[#1C1C1F] hover:text-[#F6F4F0]"
  }`;

/**
 * Campaign header rendered above every charity page.
 * Carries the logo home link plus the small nav a participant needs, and the
 * control that ends their session; the organiser (admin) screens get the logo
 * only, since their own queue navigation lives on the page.
 *
 * @returns The sticky campaign header
 */
export const CharityHeader: React.FC = () => {
  const { hasHouseholdSession, clearSession } = useCharity();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isAdminRoute = pathname.startsWith(CharityRoute.AdminQueue);
  const isCampaignClosed = useIsCampaignClosed() === true;
  const sessionNavLinks = hasHouseholdSession ? PARTICIPANT_NAV_LINKS : VISITOR_NAV_LINKS;
  const navLinks = isCampaignClosed
    ? sessionNavLinks.filter((navLink) => !CAMPAIGN_CLOSED_WITHDRAWN_ROUTES.includes(navLink.route))
    : sessionNavLinks;

  const handleSignOut = (): void => {
    clearSession();
    navigate(CharityRoute.Campaign);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-[#2C2C30] bg-[#131315]/95 backdrop-blur">
      <div className="mx-auto flex max-w-4xl flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <CharityLogo />

        {!isAdminRoute && (
          <div className="-mx-1 flex flex-wrap items-center gap-1 sm:mx-0 sm:flex-nowrap">
            <nav aria-label="Campaign" className="flex flex-wrap items-center gap-1">
              {navLinks.map((navLink) => (
                <NavLink
                  key={navLink.route}
                  to={navLink.route}
                  end={navLink.route === CharityRoute.Campaign}
                  className={({ isActive }) => linkClassName(isActive)}
                >
                  {navLink.label}
                </NavLink>
              ))}
            </nav>

            {hasHouseholdSession && (
              <Button
                type="button"
                variant="outline"
                onClick={handleSignOut}
                className={`${CAMPAIGN_QUIET_CONTROL_CLASS} flex-shrink-0`}
              >
                {CHARITY_SIGN_OUT_LABEL}
              </Button>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
